import type { GrowthDomain } from '../pages/quiz-assessment/quiz-data';

export interface ArticlePreview {
  slug: string;
  title: string;
  category: string;
  readingTime: number;
  excerpt: string;
}

// ─────────────────────────────────────────────────────────────
// Feature 1: Landing page hero articles (editorial picks)
// ─────────────────────────────────────────────────────────────

export const HERO_ARTICLES: ArticlePreview[] = [
  {
    slug: 'what-is-nonviolent-communication',
    title: "What Is Nonviolent Communication? A Beginner's Guide",
    category: 'fundamentals',
    readingTime: 9,
    excerpt:
      'The 4-step framework that transforms how you connect with the people who matter most.',
  },
  {
    slug: 'how-to-stop-arguing-with-your-partner',
    title: 'How to Stop Arguing With Your Partner (And Start Connecting)',
    category: 'relationships',
    readingTime: 11,
    excerpt:
      "The recurring fight isn't about what you think it's about — here's what's actually happening and how to break the cycle.",
  },
  {
    slug: 'self-empathy-the-skill-nobody-taught-you',
    title: 'Self-Empathy: The Communication Skill Nobody Taught You',
    category: 'self-empathy',
    readingTime: 8,
    excerpt: 'Before you can truly hear others, you need to learn to hear yourself.',
  },
];

// ─────────────────────────────────────────────────────────────
// Feature 2: Post-quiz article recommendations
// Mapped by growthDomain — 12 archetypes cluster into 7 domains
// ─────────────────────────────────────────────────────────────

export const ARCHETYPE_ARTICLES: Record<GrowthDomain, ArticlePreview[]> = {
  observations: [
    {
      slug: 'observations-vs-judgments-the-first-step-to-better-communication',
      title: 'Observations vs. Judgments: The First Step to Better Communication',
      category: 'fundamentals',
      readingTime: 8,
      excerpt:
        'Why learning to see what actually happened — without adding a story — changes everything.',
    },
    {
      slug: 'what-is-nonviolent-communication',
      title: "What Is Nonviolent Communication? A Beginner's Guide",
      category: 'fundamentals',
      readingTime: 9,
      excerpt:
        'The 4-step framework that transforms how you connect with the people who matter most.',
    },
    {
      slug: 'how-to-use-i-statements-the-right-way',
      title: 'How to Use I-Statements the Right Way',
      category: 'fundamentals',
      readingTime: 7,
      excerpt: "Most people get I-statements wrong — here's the version that actually works.",
    },
  ],
  feelings: [
    {
      slug: 'how-to-identify-your-feelings-without-blaming-others',
      title: 'How to Identify Your Feelings Without Blaming Others',
      category: 'fundamentals',
      readingTime: 8,
      excerpt:
        'The difference between actual feelings and evaluations disguised as feelings — and why it matters.',
    },
    {
      slug: 'self-empathy-the-skill-nobody-taught-you',
      title: 'Self-Empathy: The Communication Skill Nobody Taught You',
      category: 'self-empathy',
      readingTime: 8,
      excerpt: 'Before you can truly hear others, you need to learn to hear yourself.',
    },
    {
      slug: 'why-saying-i-feel-like-you-isnt-really-a-feeling',
      title: "Why Saying 'I Feel Like You...' Isn't Really a Feeling",
      category: 'fundamentals',
      readingTime: 7,
      excerpt: 'The subtle language trap that kills connection — and what to say instead.',
    },
  ],
  needs: [
    {
      slug: 'understanding-universal-human-needs-the-key-to-every-conflict',
      title: 'Understanding Universal Human Needs: The Key to Every Conflict',
      category: 'fundamentals',
      readingTime: 9,
      excerpt: 'Why every argument is really about unmet needs — and why needs never conflict.',
    },
    {
      slug: 'hidden-needs-behind-relationship-arguments',
      title: 'The Hidden Needs Behind Most Relationship Arguments',
      category: 'conflict-resolution',
      readingTime: 8,
      excerpt:
        'Money, chores, in-laws, intimacy — every recurring fight is really about something deeper.',
    },
    {
      slug: 'the-difference-between-needs-and-strategies',
      title: 'The Difference Between Needs and Strategies',
      category: 'fundamentals',
      readingTime: 7,
      excerpt:
        'The single most important distinction in Nonviolent Communication — and why it changes everything.',
    },
  ],
  sovereignty: [
    {
      slug: 'what-is-sovereignty-in-communication',
      title: 'What Is Sovereignty in Communication?',
      category: 'fundamentals',
      readingTime: 7,
      excerpt: 'How surrender and self-action transform the way you relate to yourself and others.',
    },
    {
      slug: 'how-to-set-boundaries-without-guilt',
      title: 'How to Set Boundaries Without Guilt',
      category: 'self-empathy',
      readingTime: 8,
      excerpt:
        "Boundaries aren't walls you build against people — they're bridges to what you actually need.",
    },
    {
      slug: 'how-to-stop-people-pleasing-and-start-being-honest',
      title: 'How to Stop People-Pleasing and Start Being Honest',
      category: 'self-empathy',
      readingTime: 8,
      excerpt:
        "People-pleasing isn't kindness — it's a survival strategy. Here's how to find your real voice without losing your connections.",
    },
  ],
  requests: [
    {
      slug: 'how-to-make-requests-that-actually-get-heard',
      title: 'How to Make Requests That Actually Get Heard',
      category: 'fundamentals',
      readingTime: 8,
      excerpt:
        'The art of asking for what you need — without demanding, hinting, or hoping someone reads your mind.',
    },
    {
      slug: 'how-to-resolve-conflicts-without-winners-or-losers',
      title: 'How to Resolve Conflicts Without Winners or Losers',
      category: 'conflict-resolution',
      readingTime: 8,
      excerpt:
        'The surprising truth that makes every conflict solvable: needs never conflict — only strategies do.',
    },
    {
      slug: 'how-to-give-feedback-without-triggering-defensiveness',
      title: 'How to Give Feedback Without Triggering Defensiveness',
      category: 'workplace',
      readingTime: 7,
      excerpt:
        'Replace blame and vague criticism with the observation-feeling-need-request framework that actually works.',
    },
  ],
  naturalization: [
    {
      slug: 'how-to-use-i-statements-the-right-way',
      title: 'How to Use I-Statements the Right Way',
      category: 'fundamentals',
      readingTime: 7,
      excerpt: "Most people get I-statements wrong — here's the version that actually works.",
    },
    {
      slug: 'nvc-for-couples-a-practical-guide-to-deeper-connection',
      title: 'NVC for Couples: A Practical Guide to Deeper Connection',
      category: 'relationships',
      readingTime: 10,
      excerpt: 'The communication practices that turn conflict into closeness.',
    },
    {
      slug: 'how-to-fight-fair-in-your-relationship',
      title: 'How to Fight Fair in Your Relationship',
      category: 'relationships',
      readingTime: 8,
      excerpt: 'An NVC approach to conflict that strengthens your bond instead of breaking it.',
    },
  ],
  'relationship-patterns': [
    {
      slug: 'bids-for-connection-the-small-moments-that-make-or-break-relationships',
      title: 'Bids for Connection: The Small Moments That Make or Break Relationships',
      category: 'relationships',
      readingTime: 9,
      excerpt:
        'How recognizing and responding to everyday bids determines whether your relationships thrive or wither.',
    },
    {
      slug: 'anatomy-of-a-recurring-fight',
      title: "The Anatomy of a Recurring Fight: What's Really Happening Under the Surface",
      category: 'conflict-resolution',
      readingTime: 8,
      excerpt: 'Why couples keep having the same argument — and what the fight is actually about.',
    },
    {
      slug: 'the-contempt-threshold',
      title: 'The Contempt Threshold: When Couples Stop Trying to Understand Each Other',
      category: 'conflict-resolution',
      readingTime: 9,
      excerpt:
        "The gradual shift from frustration to contempt — and why it's the strongest predictor of relationship failure.",
    },
  ],
};

// ─────────────────────────────────────────────────────────────
// Feature 3: Post-lesson "Dive Deeper" suggestions
// Mapped by course domain slug (derived from folder names)
// Domain slugs: intro, observations, feelings, needs,
//               sovereignty, requests, naturalization, relationship-patterns
// ─────────────────────────────────────────────────────────────

export const DOMAIN_ARTICLES: Record<string, ArticlePreview[]> = {
  intro: [
    ARCHETYPE_ARTICLES.observations[1], // What Is NVC
    {
      slug: 'giraffe-vs-jackal-language-two-ways-of-speaking',
      title: 'Giraffe vs. Jackal Language: Two Ways of Speaking',
      category: 'fundamentals',
      readingTime: 8,
      excerpt:
        "Marshall Rosenberg's powerful metaphor for understanding why some conversations connect and others combust.",
    },
  ],
  observations: ARCHETYPE_ARTICLES.observations.slice(0, 2),
  feelings: ARCHETYPE_ARTICLES.feelings.slice(0, 2),
  needs: ARCHETYPE_ARTICLES.needs.slice(0, 2),
  sovereignty: ARCHETYPE_ARTICLES.sovereignty.slice(0, 2),
  requests: ARCHETYPE_ARTICLES.requests.slice(0, 2),
  naturalization: ARCHETYPE_ARTICLES.naturalization.slice(0, 2),
  'relationship-patterns': ARCHETYPE_ARTICLES['relationship-patterns'].slice(0, 2),
};

// ─────────────────────────────────────────────────────────────
// Prerender slug registry
//
// `scripts/prerender.mjs` discovers article slugs to prerender by
// regex-scanning this file for `slug: '...'`. Articles surfaced
// editorially above (HERO_ARTICLES, ARCHETYPE_ARTICLES, DOMAIN_ARTICLES)
// are picked up automatically. Articles that aren't in any editorial
// mapping but still need prerendered HTML for SEO are registered here.
//
// This is purely a prerender registration — no editorial implication.
// Each article lives alongside everything else in `/learn`, served by
// the same backend, discoverable through the same listing + search,
// and tagged into its appropriate existing category (see frontmatter).
//
// Field-shape note: the prerender regex matches `slug: '...'`, so the
// entries here are objects with the same ArticlePreview shape as the
// editorial maps above. Reading time + excerpt mirror the article's
// own frontmatter and may be used by future listing surfaces.
// ─────────────────────────────────────────────────────────────

export const PRERENDER_CATALOG: ArticlePreview[] = [
  {
    slug: 'how-to-stop-an-argument',
    title: 'How to Stop an Argument in the Moment (With Anyone)',
    category: 'conflict-resolution',
    readingTime: 7,
    excerpt:
      'A script for stopping an argument while it is happening, with a partner, a parent, a coworker, or a stranger, and why it works when winning does not.',
  },
  {
    slug: 'what-is-gaslighting-and-how-nvc-helps',
    title:
      'What Is Gaslighting? Understanding the Pattern, Naming the Harm, Responding With Clarity',
    category: 'conflict-resolution',
    readingTime: 9,
    excerpt:
      'Why this kind of reality-distortion is so disorienting — and what NVC offers that goes beyond just naming it.',
  },
  {
    slug: 'what-is-narcissism-and-how-nvc-helps',
    title: 'What Is Narcissism? Recognizing the Pattern, Naming the Harm, Responding With Clarity',
    category: 'relationships',
    readingTime: 11,
    excerpt:
      'Why chronic self-centering is so disorienting to be close to — and what NVC offers that goes beyond just spotting it.',
  },
  {
    slug: 'what-is-darvo-and-how-nvc-helps',
    title:
      'What Is DARVO? Recognizing the Deny-Attack-Reverse Pattern and Responding Without Getting Pulled Under',
    category: 'conflict-resolution',
    readingTime: 13,
    excerpt:
      'Why the conversation flips and you end up apologizing — and what NVC offers beyond simply naming the move.',
  },
  {
    slug: 'what-is-weaponized-incompetence-and-how-nvc-helps',
    title:
      'What Is Weaponized Incompetence? Naming the Pattern, Understanding the Need, Responding Without a Fight',
    category: 'relationships',
    readingTime: 13,
    excerpt:
      'Why one person quietly ends up carrying the labor — and what NVC offers when the imbalance has become the arrangement.',
  },
  {
    slug: 'what-is-stonewalling-and-how-nvc-helps',
    title:
      'What Is Stonewalling? Understanding the Shutdown, Naming the Pattern, Responding Without Pushing Harder',
    category: 'conflict-resolution',
    readingTime: 12,
    excerpt:
      "Why going silent during conflict often isn't a choice — and what NVC offers that more pressure never will.",
  },
  {
    slug: 'the-four-horsemen-of-relationship-conflict',
    title: 'The Four Horsemen of Relationship Conflict (and What NVC Offers in Their Place)',
    category: 'relationships',
    readingTime: 15,
    excerpt:
      'Why these four patterns predict relationship failure — and how nonviolent communication gives you a different toolkit for each one.',
  },
  {
    slug: 'what-is-mind-reading-and-how-nvc-helps',
    title: 'What Is Mind Reading? Recognizing the Quiet Pitfall in Almost Every Relationship',
    category: 'conflict-resolution',
    readingTime: 8,
    excerpt:
      'Why assuming you know what someone is thinking gets in the way — and the NVC move that replaces it.',
  },
  {
    slug: 'what-is-score-keeping-and-how-nvc-helps',
    title: 'What Is Score-Keeping? The Tally in Your Head and What NVC Offers Instead',
    category: 'conflict-resolution',
    readingTime: 8,
    excerpt:
      'Why running a mental ledger is usually a signal of an unmet need — and the NVC move that names the need, not the score.',
  },
  {
    slug: 'what-is-conditional-affection-and-how-nvc-helps',
    title: 'What Is Conditional Affection? When Warmth Gets Used as a Lever',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      'Why warmth used as reward/punishment is structurally different from healthy space — and what NVC offers when you spot the pattern.',
  },
  {
    slug: 'what-is-future-faking-and-how-nvc-helps',
    title: 'What Is Future Faking? The Promise That Keeps Not Arriving',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      'Why promises about the future get used to extract present cooperation — and the NVC move that surfaces whether the promise is real.',
  },
  {
    slug: 'what-is-trauma-dumping-and-how-nvc-helps',
    title: 'What Is Trauma Dumping? Naming the Pattern Without Pathologizing Hard Conversations',
    category: 'relationships',
    readingTime: 10,
    excerpt:
      "Why the label has limits — and the NVC move (check capacity first) that's the actual fix.",
  },
  {
    slug: 'what-is-love-bombing-and-how-nvc-helps',
    title: 'What Is Love Bombing? Distinguishing Genuine Enthusiasm From the Setup',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      "Why intense early affection isn't the diagnostic — what comes after is — and what NVC offers when you spot the cycle.",
  },
  {
    slug: 'what-is-ghosting-and-how-nvc-helps',
    title: 'What Is Ghosting? Why It Hurts So Specifically and What NVC Offers in Its Place',
    category: 'relationships',
    readingTime: 8,
    excerpt:
      'Why the silence cuts the way it does — and the soft-no NVC offers as the alternative.',
  },
  {
    slug: 'what-is-negging-and-how-nvc-helps',
    title: 'What Is Negging? Recognizing the Backhanded Compliment for What It Is',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      'Why subtle put-downs hook the recipient — and the NVC move that refuses to engage the compliment frame.',
  },
  {
    slug: 'what-is-criticism-and-how-nvc-helps',
    title: 'What Is Criticism? The Character-Attack Pattern Underneath Most Recurring Fights',
    category: 'conflict-resolution',
    readingTime: 8,
    excerpt:
      'The difference between a criticism and a complaint — and the OFNR translation that keeps the frustration but loses the indictment.',
  },
  {
    slug: 'what-is-triangulation-and-how-nvc-helps',
    title: 'What Is Triangulation? When Conflict Routes Through a Third Person',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      'Why the messenger position feels helpful but keeps the original conflict in place — and the NVC way out.',
  },
  {
    slug: 'what-is-enmeshment-and-how-nvc-helps',
    title: 'What Is Enmeshment? When Closeness Becomes a Loss of Self',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      "Why enmeshment isn't the same as closeness — and what sovereignty (in the NVC sense) restores.",
  },
  {
    slug: 'what-is-parentification-and-how-nvc-helps',
    title: 'What Is Parentification? Recognizing the Role You Carried as a Child',
    category: 'relationships',
    readingTime: 10,
    excerpt:
      "The adult legacy of holding responsibilities that should have been a parent's — and the self-empathy that begins to name what got missed.",
  },
  {
    slug: 'what-is-scapegoating-and-how-nvc-helps',
    title: 'What Is Scapegoating? The Family Role Nobody Chose',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      "Why the family assigned someone to carry the system's anxiety — and the NVC move that names the role for what it is.",
  },
  {
    slug: 'what-is-the-golden-child-and-black-sheep-dynamic-and-how-nvc-helps',
    title: 'What Is the Golden Child / Black Sheep Dynamic? Two Roles, One System, Both Costly',
    category: 'relationships',
    readingTime: 9,
    excerpt:
      'Why both roles cost — and the NVC move that reconnects you with the person underneath the role you were cast in.',
  },
  {
    slug: 'what-is-manipulation-and-how-nvc-helps',
    title:
      'What Is Manipulation? Distinguishing It From Persuasion, Recognizing the Move, Responding Clearly',
    category: 'conflict-resolution',
    readingTime: 9,
    excerpt:
      'Why direct requests with consent are the structural opposite of manipulation — and what NVC offers when you spot the indirect ask.',
  },
  {
    slug: 'what-is-projection-and-how-nvc-helps',
    title: 'What Is Projection? Catching the Feeling You Just Relocated',
    category: 'conflict-resolution',
    readingTime: 8,
    excerpt:
      'Why projection feels like clarity but functions like avoidance — and the self-empathy practice that catches it in yourself.',
  },
  {
    slug: 'what-is-hoovering-and-how-nvc-helps',
    title: 'What Is Hoovering? When the Person Who Left Comes Pulling You Back',
    category: 'relationships',
    readingTime: 10,
    excerpt:
      'Why intermittent reinforcement makes hoovering so hard to resist — and the NVC diagnostic that distinguishes repair from the cycle.',
  },
  {
    slug: 'what-is-coercive-control-and-how-nvc-helps',
    title:
      'What Is Coercive Control? Naming the Pattern, Understanding Its Scope, and Why Communication Tools Are Not the Fix',
    category: 'relationships',
    readingTime: 10,
    excerpt:
      'Coercive control is abuse, not a communication problem. What NVC cannot do here — and what self-empathy can.',
  },
  {
    slug: 'what-is-codependency-and-how-nvc-helps',
    title: 'What Is Codependency? Holding the Label Loosely While Naming What It Points At',
    category: 'self-empathy',
    readingTime: 10,
    excerpt:
      "The diagnostic isn't whether you care for others — it's whether you know what YOU need while you do.",
  },
  {
    slug: 'what-is-trauma-bonding-and-how-nvc-helps',
    title: 'What Is Trauma Bonding? Recognizing the Attachment That Forms Inside Harm',
    category: 'relationships',
    readingTime: 10,
    excerpt:
      'Why the nervous system attaches to the source of intermittent harm — and what NVC can (and cannot) offer when you recognize the bond.',
  },
];
